import { useState } from "react";
import { GRAD } from "../../../Constants";
import styles from "./SpotDashboard.module.css";

export function PieChart({ data, labels, gradKey = "indigo", prefix = "", suffix = "", centerValue,
  centerLabel }) {
  const [hovered, setHovered] = useState(null);
  const colors = GRAD[gradKey] || GRAD.indigo;
  const total = data.reduce((sum, item) => sum + Number(item || 0), 0);

  if (!total) {
    return (
      <div className={styles["sd-empty-chart"]}>
        <span style={{ color: "#9A9A99", fontSize: "14px" }}>No data Available</span>
      </div>
    );
  }

  const cx = 100;
  const cy = 100;
  const r = 72;
  const ir = 40;
  let cumulative = 0;

  const slices = data.map((rawValue, index) => {
    const value = Number(rawValue || 0);
    const pct = value / total;
    const startAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    cumulative += pct;
    const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;

    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const xi1 = cx + ir * Math.cos(startAngle);
    const yi1 = cy + ir * Math.sin(startAngle);
    const xi2 = cx + ir * Math.cos(endAngle);
    const yi2 = cy + ir * Math.sin(endAngle);
    const largeArc = pct > 0.5 ? 1 : 0;

    // ✅ Special case: single slice at 100% — draw complete circle
    const isFull = pct >= 0.9999;
    let d;
    if (isFull) {
      // Draw two semicircles to form a complete pie
      d = [
        `M${cx} ${cy - r}`,
        `A${r} ${r} 0 1 1 ${cx} ${cy + r}`,
        `A${r} ${r} 0 1 1 ${cx} ${cy - r}`,
      ].join(" ");
    } else {
      d = `M${x1} ${y1}A${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}L${xi2} ${yi2}A${ir} ${ir} 0 ${largeArc} 0 ${xi1} ${yi1}Z`;
    }

    return {
      d,
      pct,
      value,
      color: colors[index % colors.length],
      isFull,
    };
  });

  const hoveredSlice = hovered !== null ? slices[hovered] : null;

  return (
    <svg viewBox="0 0 200 200" className={styles["sd-pie-svg"]}>
      {slices.map((slice, index) => (
        <path
          key={index}
          d={slice.d}
          fill={slice.color}
          stroke="#FEFEFE"
          strokeWidth="2"
          opacity={hovered === null || hovered === index ? 1 : 0.38}
          onMouseEnter={() => setHovered(index)}
          onMouseLeave={() => setHovered(null)}
        />
      ))}

      {!hoveredSlice && (
        <>
          <text
            x={cx}
            y={cy - 5}
            textAnchor="middle"
            fontSize="14"
            fontWeight="600"
            fill="#ff7e00"
          >
            {centerValue || `${prefix}${total.toFixed(2)}${suffix}`}
          </text>

          <text
            x={cx}
            y={cy + 11}
            textAnchor="middle"
            fontSize="8.5"
            fill="#ff7e00"
          >
            {centerLabel || "total spend"}
          </text>
        </>
      )}

      {hoveredSlice && (() => {
        const label = labels[hovered] || "";
        // Split into lines of ~18 chars max to fit the 90px wide box
        // Also handle long words by breaking them if they exceed char limit
        const words = label.split(" ");
        const lines = [];
        let current = "";
        for (const word of words) {
          // If a single word is longer than 18 chars, break it
          if (word.length > 16) {
            if (current) {
              lines.push(current);
              current = "";
            }
            // Break long word into chunks of 18 chars
            for (let i = 0; i < word.length; i += 16) {
              lines.push(word.substring(i, i + 16));
            }
          } else {
            const test = current ? `${current} ${word}` : word;
            if (test.length > 18 && current) {
              lines.push(current);
              current = word;
            } else {
              current = test;
            }
          }
        }


        if (current) lines.push(current);

        const lineH = 13;
        const nameH = lines.length * lineH;
        const rectH = 18 + nameH + 28; // top pad + name block + value + pct
        const rectY = 78;
        const nameStartY = rectY + 14;
        const valueY = nameStartY + nameH + 2;
        const pctY = valueY + 12;

        return (
          <g className={styles["sd-svg-tooltip"]}>
            <rect x="55" y={rectY} width="90" height={rectH} rx="7" fill="#FEFEFE" stroke={hoveredSlice.color} />
            {lines.map((line, i) => (
              <text
                key={i}
                x="100"
                y={nameStartY + i * lineH}
                textAnchor="middle"
                fontSize="9.5"
                fontWeight="600"
                fill="#2B2A29"
              >
                {line}
              </text>
            ))}
            <text x="100" y={valueY} textAnchor="middle" fontSize="10" fontWeight="700" fill={hoveredSlice.color}>
              {prefix}{hoveredSlice.value.toFixed(2)}{suffix}
            </text>
            <text x="100" y={pctY} textAnchor="middle" fontSize="8.5" fill="#727271">
              {Math.round(hoveredSlice.pct * 100)}% of total
            </text>
          </g>
        );
      })()}
    </svg>
  );
}